import {
  takeLatest,
  put,
  call,
  takeEvery,
  select,
  all,
} from 'redux-saga/effects';
import { initialize, change, reset, untouch } from 'redux-form';
import reduxFormActions from 'redux-form/lib/actions';

import { showProgressBar, hideProgressBar } from '../../actions';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import * as actions from '../../actions/library/bundlePackage';
import {
  Bundles,
  FormItemEntity,
  Outlets,
  SalesTypesEntity,
  User,
  Image,
} from '../../actions/library/bundlePackage/types';
import * as actNotif from '../../actions/notifications';
import { resetUploadImage } from '../../actions/uploadImage';

import { outlets as apiGetOutlets } from '../../api/outlets';
import * as api from '../../api/libraries/bundlePackage';
import {
  getBundlesListArray,
  getBundleItemVariants,
  getFiltersFormValues,
  getBundleFormValues,
} from '../../selectors/libraries/bundlePackage';
import { useMokaOutletPropsFirst } from '../../utils/formatter';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapEvent from '../../clevertap/library/bundlePackage';

const { updateSyncErrors } = reduxFormActions;

const BUNDLE_NAME_ALREADY_EXISTS_ERROR_MESSAGE =
  'Bundle name already exists in your business, please use another name';
const CREATE_BUNDLE_CLEVERTAP_ACTION_NAME = 'create';
const UPDATE_BUNDLE_CLEVERTAP_ACTION_NAME = 'update';

type OutletsForm = Outlets & FormItemEntity;
type SalesTypesForm = SalesTypesEntity & FormItemEntity;

function* resetPage() {
  yield put(actions.setOutlets([]));
  yield put(actions.resetBundlesListState());
  yield put(
    initialize('bundlePackage', {
      isSalesTypeEnabled: false,
      basicPrice: null,
      salesTypes: {},
      bundleComponents: [],
    })
  );
  yield put(initialize('bundlePackageActive', { isActive: true }));
  yield put(
    initialize('bundlePackageAssignItem', {
      searchKeyword: '',
      bundleItems: '',
      itemVariant: '',
    })
  );
}

function* fillForm(bundle: Bundles) {
  const formName = 'bundlePackage';

  const salesTypes = {};
  bundle.sales_types?.forEach((s) => {
    salesTypes[s.name!] = s.price;
  });

  yield put(
    initialize(formName, {
      id: bundle.id,
      name: bundle.name,
      image: bundle.image,
      isSalesTypeEnabled: bundle.sales_types!.length > 0,
      basicPrice: bundle.basic_price,
      bundleComponents: bundle.bundle_components?.map((item) => {
        return {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          item_name: item.item_name,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          item_variant_name:
            item.item_variant_name !== null
              ? item.item_variant_name
              : 'All Variants',
          quantity: item.quantity,
        };
      }),
      salesTypes,
      isComponentChanged: false,
    })
  );

  yield put(initialize('bundlePackageActive', { isActive: bundle.is_active }));
}

function* getBundleSalesTypes(assignedOutletIds: number[]) {
  if (assignedOutletIds.length === 0) {
    return [];
  }

  try {
    const { data } = yield call(api.getIntersectSalesTypes, {
      assignedOutletIds,
    });
    return data;
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotif.error(e.message));
  }
}

function* setSalesTypeFromOutletIds(assignedOutlets: OutletsForm[]) {
  const checkedOutletIds = assignedOutlets.map((o) => o.id) as number[];

  const salesTypeData = yield getBundleSalesTypes(checkedOutletIds);

  const salesTypes = salesTypeData.map((salesType) => ({
    name: salesType,
    isChecked: true,
    isShowed: true,
  }));

  yield put(
    initialize('bundlePackageManageSalesType', {
      salesTypes,
    })
  );

  yield put(actions.setAssignedBundleSalesTypes(salesTypes));
  yield put(actions.setSalesTypes(salesTypes));
  yield all(
    salesTypeData.map((name) => {
      return all([
        // TODO: https://go-jek.atlassian.net/browse/PPF-475
        // @ts-expect-error Expected 3-5 arguments, but got 2.ts(2554)
        put(change('bundlePackage', `salesTypes[${name}]`)),
        put(untouch('bundlePackage', `salesTypes[${name}]`)),
      ]);
    })
  );
}

function* setOutletBundle(selectedOutlets: OutletsForm[]) {
  const checkedOutlets = selectedOutlets.filter((o) => o.isChecked);
  const checkedOutletIds = checkedOutlets.map(
    (checkedOutlet) => checkedOutlet.id
  );
  const { data } =
    checkedOutletIds.length === 0
      ? { data: [] }
      : yield call(api.getUnionItemNames, {
          assignedOutletIds: checkedOutletIds,
        });

  yield put(actions.setComponentsItemName({ itemName: data, page: 1 }));
  yield put(actions.setAssignedBundleOutlets(checkedOutlets));
  yield put(actions.setSelectedOutlets(selectedOutlets));
  yield put(actions.hideBundleAssignOutletModal());
  yield put(actions.hideChangeOutletSalesTypeModal());

  yield call(setSalesTypeFromOutletIds, checkedOutlets);
}

function* setBundleListState(responseData: any, prependWith: Bundles[] = []) {
  yield put(actions.setIsCompleted(responseData.is_completed));
  yield put(actions.setNextCursor(responseData.next_cursor));
  yield put(actions.setBundlesList(prependWith.concat(responseData.results)));
}

function* loadAllItemVariants(outletIds: number[], itemName: string) {
  let currentPage = 1;
  let itemVariantsData = [];
  const { data } = yield call(api.getUnionItemVariantNames, {
    assignedOutletIds: outletIds,
    itemName,
    page: currentPage,
  });

  itemVariantsData = itemVariantsData.concat(data.results);

  let { completed } = data;

  while (!completed) {
    currentPage += 1;
    const result = yield call(api.getUnionItemVariantNames, {
      assignedOutletIds: outletIds,
      itemName,
      page: currentPage,
    });

    completed = result.data.completed;
    itemVariantsData = itemVariantsData.concat(result.data.results);
  }

  return itemVariantsData;
}

function* setBundleManageSalesTypes(salesTypes: SalesTypesForm[]) {
  yield all(
    salesTypes.map((o, idx) => {
      return all([
        put(
          change(
            'bundlePackageManageSalesType',
            `salesTypes[${idx}].isChecked`,
            o.isChecked
          )
        ),
        put(
          change(
            'bundlePackageManageSalesType',
            `salesTypes[${idx}].isShowed`,
            true
          )
        ),
      ]);
    })
  );
}

function* showSubmitBundleErrors(
  error,
  bundle: Bundles,
  user: User,
  action: string
) {
  if (error.message === BUNDLE_NAME_ALREADY_EXISTS_ERROR_MESSAGE) {
    yield put(
      // TODO: https://go-jek.atlassian.net/browse/PPF-475
      // @ts-expect-error Expected 3 arguments, but got 2.ts(2554)
      updateSyncErrors('bundlePackage', {
        name: 'Bundle name already exists in your business',
      })
    );

    clevertapEvent.libraryBundlePackageNameErrorUniqueness(
      bundle,
      user,
      action
    );
  }

  yield put(actNotif.error(error.message));
}

export function* pageRequest() {
  yield call(resetPage);
  yield put(actions.unsetBundleDetail());
  yield put(showPageLoadingSpinner(true));
  yield put(showProgressBar());
  try {
    const { data: outletsData } = yield call(apiGetOutlets);
    yield put(actions.setOutlets(outletsData));

    const { data } = yield call(api.getBundlePackages);
    yield call(setBundleListState, data);

    clevertapEvent.libraryBundlePackageVisit();
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotif.error(e.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
    yield put(hideProgressBar());
  }
}

export function* loadMoreBundles(action) {
  yield put(showProgressBar());
  const { payload: nextCursor }: { payload: string } = action;
  const currentList = yield select(getBundlesListArray);
  const filters = yield select(getFiltersFormValues);
  try {
    const { data } = yield call(api.getBundlePackages, { nextCursor, filters });
    yield call(setBundleListState, data, currentList);
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotif.error(e.message));
  } finally {
    yield put(hideProgressBar());
  }
}

export function* updateListFilters(action) {
  const { payload: filters }: { payload: string[] } = action;

  const params = {
    filters: {
      outletId: filters,
    },
  };

  yield put(showPageLoadingSpinner(true));
  yield put(showProgressBar());
  try {
    const { data } = yield call(api.getBundlePackages, params);
    yield call(setBundleListState, data);
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotif.error(e.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
    yield put(hideProgressBar());
  }
}

export function* setDetailBundlePackage(action) {
  const { bundle, outlets }: { bundle: Bundles; outlets: Outlets[] } =
    action.payload;
  const { data } = yield call(api.getBundlePackageDetail, bundle.id);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { outlet_ids } = data;
  const itemVariantsUnion = {};

  // eslint-disable-next-line @typescript-eslint/naming-convention
  for (const { item_name } of data.bundle_components) {
    const res = yield call(api.getUnionItemVariantNames, {
      assignedOutletIds: data.outlet_ids,
      itemName: item_name,
      page: 1,
    });

    const filteredData = res.data.results.filter((d) => d);
    if (filteredData.length > 0) {
      itemVariantsUnion[item_name] = {
        ...res.data,
        results: ['All Variants', ...filteredData],
      };
    }
  }

  yield put(actions.setComponentsItemVariantName(itemVariantsUnion));

  const itemNameUnion =
    outlet_ids.length === 0
      ? {
          data: {
            completed: true,
            results: [],
          },
        }
      : yield call(api.getUnionItemNames, {
          assignedOutletIds: outlet_ids,
        });

  yield put(showProgressBar());
  yield put(actions.setBundleDetail(data));
  yield call(fillForm, data);
  yield put(
    actions.setComponentsItemName({ itemName: itemNameUnion.data, page: 1 })
  );

  yield put(
    initialize('bundlePackageAssignItem', {
      searchKeyword: '',
      bundleItems: '',
      itemVariant: '',
    })
  );

  const selectedOutletsForm = {
    selectedOutlets: outlets.map((outlet) => ({
      id: outlet.id,
      name: useMokaOutletPropsFirst(outlet, (o) => o.name),
      isChecked: bundle.outlet_ids!.some((id) => id === outlet.id),
      isShowed: true,
    })),
  };

  const assignedOutlets = selectedOutletsForm.selectedOutlets.filter(
    (o) => o.isChecked
  );

  yield put(
    initialize('bundlePackageAssignOutlet', {
      selectedOutlets: selectedOutletsForm.selectedOutlets,
    })
  );
  yield put(actions.setAssignedBundleOutlets(assignedOutlets));
  yield put(actions.setSelectedOutlets(selectedOutletsForm.selectedOutlets));

  const isSalesTypeEnabled = data.sales_types.length > 0;
  const assignedSalesTypes = data.sales_types.map((o) => ({
    ...o,
    isChecked: true,
  }));
  const salesTypeData = yield getBundleSalesTypes(outlet_ids);
  const salesTypes = salesTypeData.map((salesType) => ({
    name: salesType,
    isChecked: data.sales_types.some((s) => s.name === salesType),
    isShowed: true,
  }));

  yield put(
    initialize('bundlePackageManageSalesType', {
      salesTypes,
    })
  );
  yield put(actions.setAssignedBundleSalesTypes(assignedSalesTypes));
  yield put(actions.setSalesTypes(salesTypes));
  if (isSalesTypeEnabled) {
    yield all(
      salesTypeData.map((name) =>
        // TODO: https://go-jek.atlassian.net/browse/PPF-475
        // @ts-expect-error Expected 3-5 arguments, but got 2.ts(2554)
        put(change('bundlePackage', `salesTypes[${name}]`))
      )
    );
  }
}

export function* onClickCreate(action) {
  const { payload: outlets }: { payload: Outlets[] } = action;

  const selectedOutletsForm = {
    selectedOutlets: outlets.map((outlet) => ({
      id: outlet.id,
      name: useMokaOutletPropsFirst(outlet, (o) => o.name),
      isChecked: false,
      isShowed: true,
    })),
  };

  yield put(actions.setSelectedOutlets(selectedOutletsForm.selectedOutlets));
  yield put(actions.setAssignedBundleSalesTypes([]));
  yield put(actions.setSalesTypes([]));
  yield put(initialize('bundlePackageAssignOutlet', selectedOutletsForm));
  yield put(
    initialize('bundlePackage', {
      isSalesTypeEnabled: false,
      basicPrice: null,
      salesTypes: {},
      bundleComponents: [],
    })
  );
  yield put(initialize('bundlePackageActive', { isActive: true }));
  yield put(
    initialize('bundlePackageAssignItem', {
      searchKeyword: '',
      bundleItems: '',
      itemVariant: '',
    })
  );
  try {
    yield put(actions.unsetBundleDetail());
    yield put(showProgressBar());
    yield put(actions.setInitialBundle());
  } finally {
    yield put(hideProgressBar());
  }
}

export function* searchOutlet(action) {
  const { outlets, query }: { outlets: Outlets[]; query: string } =
    action.payload;
  yield all(
    outlets.map((outlet, idx) => {
      return put(
        change(
          'bundlePackageAssignOutlet',
          `selectedOutlets[${idx}].isShowed`,
          useMokaOutletPropsFirst(outlet, (o) => o.name)
            .toLowerCase()
            .includes(query.toLowerCase())
        )
      );
    })
  );
}

export function* selectAllOutlets(action) {
  const {
    selectedOutlets,
    isCheckedAll,
  }: { selectedOutlets: OutletsForm[]; isCheckedAll: boolean } = action.payload;
  yield all(
    selectedOutlets.map(
      ({ isShowed }, idx) =>
        isShowed &&
        put(
          change(
            'bundlePackageAssignOutlet',
            `selectedOutlets[${idx}].isChecked`,
            isCheckedAll
          )
        )
    )
  );
}

export function* onClickCancelOutletAssign(action) {
  const { payload: previousSelectedOutlets }: { payload: OutletsForm[] } =
    action;
  yield all(
    previousSelectedOutlets.map((o, idx) => {
      return all([
        put(
          change(
            'bundlePackageAssignOutlet',
            `selectedOutlets[${idx}].isChecked`,
            o.isChecked
          )
        ),
        put(
          change(
            'bundlePackageAssignOutlet',
            `selectedOutlets[${idx}].isShowed`,
            true
          )
        ),
      ]);
    })
  );
  yield put(actions.hideBundleAssignOutletModal());
}

export function* unsetBundleDetail() {
  yield put(resetUploadImage());
  yield put(reset('bundlePackageAssignOutlet'));
  yield put(reset('bundlePackage'));
  yield put(reset('bundlePackageAssignItem'));
}

export function* onClickAssignSalesType() {
  yield put(actions.showBundleManageSalesTypeModal());
}

export function* onSalesTypeSearchChange(action) {
  const { salesTypes, query }: { salesTypes: SalesTypesForm[]; query: string } =
    action.payload;

  yield all(
    salesTypes.map((salesType, idx) => {
      return put(
        change(
          'bundlePackageManageSalesType',
          `salesTypes[${idx}].isShowed`,
          salesType.name?.toLowerCase().includes(query.toLowerCase())
        )
      );
    })
  );
}

export function* changeAllSalesType(action) {
  const {
    salesTypes,
    isCheckedAll,
  }: { salesTypes: SalesTypesForm[]; isCheckedAll: boolean } = action.payload;
  yield all(
    salesTypes.map(
      ({ isShowed }, idx) =>
        isShowed &&
        put(
          change(
            'bundlePackageManageSalesType',
            `salesTypes[${idx}].isChecked`,
            isCheckedAll
          )
        )
    )
  );
}

export function* submitSalesType(action) {
  const { payload: salesTypes }: { payload: SalesTypesForm[] } = action;

  const assignedSalesTypes = salesTypes.filter((s) => s.isChecked);
  yield put(actions.setAssignedBundleSalesTypes(assignedSalesTypes));
  yield setBundleManageSalesTypes(salesTypes);
  yield all([
    salesTypes.map(
      (s) =>
        !s.isChecked &&
        put(change('bundlePackage', `salesTypes[${s.name}]`, ''))
    ),
  ]);

  yield put(actions.hideBundleManageSalesTypeModal());
}

export function* onClickCancelManageSalesType(action) {
  const { payload: previousSelectedSalesTypes }: { payload: SalesTypesForm[] } =
    action;
  yield setBundleManageSalesTypes(previousSelectedSalesTypes);
  yield put(actions.hideBundleManageSalesTypeModal());
}

export function* enableBundleSalesType(action) {
  const {
    isEnabled,
    assignedOutlets,
    previousAssignedSalesTypes,
  }: {
    isEnabled: boolean;
    assignedOutlets: OutletsForm[];
    previousAssignedSalesTypes: SalesTypesForm[];
  } = action.payload;

  if (!isEnabled) {
    yield all(
      previousAssignedSalesTypes.map((s) => {
        return all([
          put(change('bundlePackage', `salesTypes[${s.name}]`, '')),
          put(untouch('bundlePackage', `salesTypes[${s.name}]`)),
        ]);
      })
    );
    return;
  }

  try {
    yield put(showProgressBar());
    yield call(setSalesTypeFromOutletIds, assignedOutlets);
    yield put(untouch('bundlePackage', 'basicPrice'));
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotif.error(e.message));
  } finally {
    yield put(hideProgressBar());
  }
}

export function* refreshBundleList(action) {
  const { outletId }: { outletId: string } = action.payload;
  const params = {
    page: 1,
    filters: {
      outletId,
    },
  };

  try {
    const { data } = yield call(api.getBundlePackages, params);
    yield call(setBundleListState, data);
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotif.error(e.message));
  }
}

export function* onSubmitCreateBundle(action) {
  const { bundle, image, user }: { bundle: Bundles; image: Image; user: User } =
    action.payload;

  try {
    if (image.file) {
      const { data } = yield call(api.uploadBundleImage, image.file);
      bundle.image = data.url;
      bundle.image_file_id = data.id;
    }

    yield call(api.createBundle, bundle);
    yield put(actNotif.success(`"${bundle.name}" was successfully created.`));
    yield unsetBundleDetail();
    yield put(actions.hideBundleAssignOutletModal());
    yield put(
      actions.refreshList({ outletId: user.outletId!, isShowingDetail: false })
    );
    clevertapEvent.libraryBundlePackageCreate(bundle, user);
  } catch (error) {
    yield showSubmitBundleErrors(
      error,
      bundle,
      user,
      CREATE_BUNDLE_CLEVERTAP_ACTION_NAME
    );
  } finally {
    yield put(hideProgressBar());
  }
}

export function* onSubmitUpdateBundle(action) {
  const {
    oldBundle,
    bundle,
    image,
    user,
  }: { oldBundle: Bundles; bundle: Bundles; image: Image; user: User } =
    action.payload;
  try {
    if (image.file) {
      const { data } = yield call(api.uploadBundleImage, image.file);
      bundle.image = data.url;
      bundle.image_file_id = data.id;
    }
    yield call(api.updateBundle, bundle);
    yield put(actNotif.success(`"${bundle.name}" was successfully updated.`));
    yield unsetBundleDetail();
    yield put(actions.hideBundleAssignOutletModal());
    yield put(
      actions.refreshList({ outletId: user.outletId!, isShowingDetail: false })
    );
    clevertapEvent.libraryBundlePackageUpdate(oldBundle, bundle, user);
  } catch (error) {
    yield showSubmitBundleErrors(
      error,
      bundle,
      user,
      UPDATE_BUNDLE_CLEVERTAP_ACTION_NAME
    );
  } finally {
    yield put(hideProgressBar());
  }
}

export function* askDeleteBundle(action) {
  const { payload: deletedBundle }: { payload: Bundles } = action;
  yield put(actions.setDeletedBundle(deletedBundle));
  yield put(actions.showDeleteBundleModal());
}

export function* cancelDeleteBundle() {
  yield put(actions.hideDeleteBundleModal());
}

export function* onSubmitDeleteBundle(action) {
  const { bundle, businessId }: { bundle: Bundles; businessId: number } =
    action.payload;

  const filters = yield select(getFiltersFormValues);
  const outletId = filters ? filters.outletId : '';

  yield put(showProgressBar());
  try {
    yield call(api.deleteBundlePackage, bundle.id);
    yield put(actNotif.success(`"${bundle.name}" was successfully deleted.`));
    clevertapEvent.libraryBundlePackageDelete(bundle, businessId);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(actions.unsetBundleDetail());
    yield put(actions.hideDeleteBundleModal());
    yield put(actions.refreshList({ outletId, isShowingDetail: false }));
  }
}

export function* updateBundleStatus(action) {
  const {
    isActive,
    bundle,
    outletId,
  }: { bundle: Bundles; isActive: boolean; outletId: string } = action.payload;

  yield put(showProgressBar());
  try {
    const newStatus = isActive ? 'shown' : 'hidden';
    yield call(api.updateBundleStatus, bundle.id, isActive);
    yield put(
      actNotif.success(`"${bundle.name}" was successfully ${newStatus}`)
    );
    yield put(actions.hideUpdateBundleStatusConfirmation(isActive));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(actions.refreshList({ outletId, isShowingDetail: true }));
    // eslint-disable-next-line @typescript-eslint/naming-convention
    yield call(fillForm, { ...bundle, is_active: isActive });
    yield put(hideProgressBar());
  }
}

export function* resetBundleStatus(action) {
  const { payload: initialStatus }: { payload: boolean } = action;
  yield put(change('bundlePackageActive', 'isActive', initialStatus));
}

export function* onChangeBundleOutlets(action) {
  const {
    selectedOutlets,
    assignedSalesTypes,
  }: { selectedOutlets: OutletsForm[]; assignedSalesTypes: SalesTypesForm[] } =
    action.payload;
  const bundleForm = yield select(getBundleFormValues);
  const checkedOutletIds = selectedOutlets
    .filter((o) => o.isChecked)
    .map((checkedOutlet) => checkedOutlet.id) as number[];

  const salesTypeData = yield getBundleSalesTypes(checkedOutletIds);

  const salesTypes = salesTypeData.map((salesType) => ({
    name: salesType,
    isChecked: true,
    isShowed: true,
  }));

  const prevSalesTypeNames = assignedSalesTypes.map(
    (salesType) => salesType.name
  );
  const currentSalesTypeNames = salesTypes.map((salesType) => salesType.name);
  const isSalesTypeNotChanged = prevSalesTypeNames.every((salesTypeName) =>
    currentSalesTypeNames.includes(salesTypeName)
  );

  if (currentSalesTypeNames.length < 1) {
    yield put(actions.disableSalesTypes);
    yield put(change('bundlePackage', 'isSalesTypeEnabled', false));
  }

  if (bundleForm.id && !isSalesTypeNotChanged) {
    yield put(actions.hideBundleAssignOutletModal());
    yield put(actions.showChangeOutletSalesTypeModal());
  } else {
    yield call(setOutletBundle, selectedOutlets);
    if (!isSalesTypeNotChanged) {
      yield put(change('bundlePackage', 'salesTypes', {}));
    }
  }
  yield all(
    selectedOutlets.map((o, idx) => {
      return all([
        put(
          change(
            'bundlePackageAssignOutlet',
            `selectedOutlets[${idx}].isShowed`,
            true
          )
        ),
      ]);
    })
  );
}

export function* onClickItemName(action) {
  const { outletIds, itemName }: { outletIds: number[]; itemName: string } =
    action.payload;
  yield put(change('bundlePackageAssignItem', 'itemVariant', ''));
  let currentItemVariant = yield select(getBundleItemVariants);
  if (!currentItemVariant[itemName]) {
    const itemVariantsData = yield call(
      loadAllItemVariants,
      outletIds,
      itemName
    );

    const filteredData = itemVariantsData.filter((d) => d);
    const isVariantExists = filteredData.length > 0;
    let results: string[] = [];

    if (isVariantExists) {
      results = ['All Variants', ...filteredData];
    }

    currentItemVariant = {
      ...currentItemVariant,
      [itemName]: {
        completed: true,
        results,
      },
    };
  }
  if (currentItemVariant[itemName].results.length > 0) {
    yield put(change('bundlePackageAssignItem', 'itemVariant', 'All Variants'));
  }
  yield put(actions.setComponentsItemVariantName(currentItemVariant));
}

export function* onConfirmChangeSalesTypeOutlets(action) {
  const { payload: selectedOutlets }: { payload: OutletsForm[] } = action;
  yield call(setOutletBundle, selectedOutlets);
  yield put(change('bundlePackage', 'salesTypes', {}));
}

export function* loadMoreBundleItems(action) {
  yield put(showProgressBar());
  const {
    previousBundleItems,
    outletIds,
    page,
    searchKeyword,
  }: {
    previousBundleItems: any[];
    outletIds: number[];
    page: number;
    searchKeyword: string;
  } = action.payload;
  try {
    const itemNameUnion =
      outletIds.length === 0
        ? {
            data: {
              completed: true,
              results: [],
            },
          }
        : yield call(api.getUnionItemNames, {
            assignedOutletIds: outletIds,
            page,
            searchKeyword,
          });

    const item = previousBundleItems.concat(itemNameUnion.data.results);

    const itemName = { results: item, completed: itemNameUnion.data.completed };
    yield put(actions.setComponentsItemName({ itemName, page }));
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotif.error(e.message));
  } finally {
    yield put(hideProgressBar());
  }
}

export function* onOpenBundleItemModal(action) {
  const {
    outletIds,
    searchKeyword,
    index,
  }: { searchKeyword: string; outletIds: number[]; index: number } =
    action.payload;
  const formValues = {
    searchKeyword: '',
    bundleItems: '',
    itemVariant: '',
  };

  if (index >= 0) {
    const bundleForm = yield select(getBundleFormValues);
    formValues.bundleItems = bundleForm.bundleComponents[index].item_name;
    formValues.itemVariant =
      bundleForm.bundleComponents[index].item_variant_name;
  }

  if (searchKeyword !== '') {
    const { data } =
      outletIds.length === 0
        ? { data: [] }
        : yield call(api.getUnionItemNames, {
            assignedOutletIds: outletIds,
          });

    yield put(actions.setComponentsItemName({ itemName: data, page: 1 }));
  }
  yield put(initialize('bundlePackageAssignItem', formValues));
}

export function* onSubmitBundleItems(action) {
  const {
    itemName,
    itemVariantName,
    index,
  }: { itemName: string; itemVariantName: string; index: number } =
    action.payload;
  const bundleForm = yield select(getBundleFormValues);
  const bundleComponent: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    item_name: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    item_variant_name?: string;
  }[] = [];
  bundleComponent[0] = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    item_name: itemName,
  };
  if (itemVariantName) {
    bundleComponent[0].item_variant_name = itemVariantName;
  }
  if (index < 0) {
    yield put(
      change(
        'bundlePackage',
        'bundleComponents',
        bundleForm.bundleComponents.concat(bundleComponent)
      )
    );
  } else {
    yield put(
      change(
        'bundlePackage',
        `bundleComponents['${index}']`,
        bundleComponent[0]
      )
    );
  }
  yield put(actions.closeBundleItemModal());
}

export function* onRemoveBundleItems(action) {
  const { payload: itemIndex }: { payload: number } = action;
  const bundleForm = yield select(getBundleFormValues);
  const components = [...bundleForm.bundleComponents];
  components.splice(itemIndex, 1);
  yield put(change('bundlePackage', 'bundleComponents', components));
  yield put(change('bundlePackage', 'isComponentChanged', true));
}

export default function* bundlePackage() {
  yield takeLatest(actions.pageRequest, pageRequest);
  yield takeEvery(actions.loadMoreBundles, loadMoreBundles);
  yield takeLatest(actions.updateListFilters, updateListFilters);
  yield takeLatest(actions.onClickRow, setDetailBundlePackage);
  yield takeLatest(actions.onClickCreateBundle, onClickCreate);
  yield takeLatest(actions.searchOutlet, searchOutlet);
  yield takeLatest(actions.selectAllOutlets, selectAllOutlets);
  yield takeLatest(
    actions.onClickCancelOutletAssign,
    onClickCancelOutletAssign
  );
  yield takeLatest(actions.unsetBundleDetail, unsetBundleDetail);
  yield takeLatest(actions.onClickAssignSalesType, onClickAssignSalesType);
  yield takeLatest(actions.onSalesTypeSearchChange, onSalesTypeSearchChange);
  yield takeLatest(actions.changeAllSalesType, changeAllSalesType);
  yield takeLatest(actions.onSubmitAssignSalesType, submitSalesType);
  yield takeLatest(actions.setIsEnabledBundleSalesTypes, enableBundleSalesType);
  yield takeLatest(
    actions.onClickCancelManageSalesType,
    onClickCancelManageSalesType
  );
  yield takeLatest(actions.askDeleteBundle, askDeleteBundle);
  yield takeLatest(actions.cancelDeleteBundle, cancelDeleteBundle);
  yield takeLatest(actions.deleteBundle, onSubmitDeleteBundle);
  yield takeLatest(actions.onSubmitCreateBundle, onSubmitCreateBundle);
  yield takeLatest(actions.onSubmitUpdateBundle, onSubmitUpdateBundle);
  yield takeLatest(actions.refreshList, refreshBundleList);
  yield takeLatest(actions.updateBundleStatus, updateBundleStatus);
  yield takeLatest(
    actions.hideUpdateBundleStatusConfirmation,
    resetBundleStatus
  );
  yield takeLatest(actions.onChangeOutletSalesType, onChangeBundleOutlets);
  yield takeLatest(
    actions.confirmChangeOutletSalesType,
    onConfirmChangeSalesTypeOutlets
  );
  yield takeLatest(actions.onClickItemName, onClickItemName);
  yield takeLatest(actions.loadMoreBundleItems, loadMoreBundleItems);
  yield takeLatest(actions.onSubmitBundleItems, onSubmitBundleItems);
  yield takeLatest(actions.addNewItem, onOpenBundleItemModal);
  yield takeLatest(actions.onRemoveBundleItems, onRemoveBundleItems);
}
